import { Box, CircularProgress, Stack } from '@mui/material';
import { compact } from 'lodash';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import useLocalStorage from 'react-use/lib/useLocalStorage';

import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Coachmark } from '@/components/poppers/Coachmark/Coachmark';
import {
  incrementCoachmarkSeenCount,
  shouldShowCoachmark,
} from '@/components/poppers/Coachmark/coachmarkUtils';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { useUnguardedMemoDetails } from '@/modules/memos/MemoDetails/contexts/MemoDetails.context';
import { DocumentSearchResult } from '@/modules/pdf/DocumentSearch/DocumentSearch.types';
import { useAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
// this is an appropriate usage of a page-specific import, because we're enabling functionality specifically
// on that page
// eslint-disable-next-line luminary-rules/no-page-imports
import { useUnguardedDocumentDetails } from '@/pages/DocumentDetailsPage/contexts/DocumentDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { diagnostics } from '@/utils/diagnostics';
import { makeUnprintableStyle } from '@/utils/makeUnprintableStyle';

import { useCitationAnnotationsLazyQuery } from './graphql/CitationNode.generated';

interface CitationPopperProps {
  paragraphIds: string[];
}

export function CitationPopper({ paragraphIds }: CitationPopperProps) {
  const { showFeedback } = useFeedback();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const documentDetailsContext = useUnguardedDocumentDetails();
  const memoDetailsContext = useUnguardedMemoDetails();
  // We'll keep this for backward compatibility but will use the shouldShowCoachmark utility directly
  const [coachmarkSeen, setCoachmarkSeen] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.COACHMARK_CITATION_TOOLTIP
  );
  const {
    documentId,
    annotations,
    setOverrideDocumentId,
    setAnnotations,
    setActiveAnnotationIndex,
    moveActiveAnnotation,
    setSearchQuery,
    setSearchLoading,
    currentCitationRequestId,
    incrementCitationRequestId,
    showCitations,
    showCitationCoachmark,
    lastCitationParagraphIdsRef,
  } = useAnnotatedPDFViewerContext();
  const [searchCitationAnnotations, { loading }] =
    useCitationAnnotationsLazyQuery({
      onError: () => {
        showFeedback('Failed to fetch citations. Please try again.');
      },
    });

  // Reset the override document ID when the component unmounts
  useEffect(() => {
    return () => {
      setOverrideDocumentId(null);
    };
  }, [setOverrideDocumentId]);

  // This handles clicks on the document-level citation button
  const searchDocumentForParagraphIds = useCallback(
    (paragraphIds: string[]) => {
      diagnostics.debug('[CITATIONS] Looking for citations', {
        paragraphIds,
        documentId,
      });

      // Check if we're searching for the same paragraphs
      const isSameParagraphs =
        JSON.stringify(paragraphIds) ===
        JSON.stringify(lastCitationParagraphIdsRef.current);

      // If searching for the same paragraphs, just cycle to the next annotation instead of refetching
      if (isSameParagraphs && annotations?.length) {
        diagnostics.debug(
          `[CITATIONS] Same paragraph IDs, cycling to next annotation`,
          {
            paragraphIds: JSON.stringify(paragraphIds),
            lastParagraphIds: JSON.stringify(
              lastCitationParagraphIdsRef.current
            ),
          }
        );
        moveActiveAnnotation(1);
        return;
      }

      // clear out the existing annotations
      setAnnotations(undefined);

      if (!paragraphIds.length) {
        diagnostics.debug(`[CITATIONS] No paragraph IDs to search`);
        return;
      }

      // Get a new request ID from the context to track this specific request
      const thisRequestId = incrementCitationRequestId();
      diagnostics.debug(
        `[CITATIONS] Searching for citations with request ID ${thisRequestId}; current request ID is ${currentCitationRequestId}`
      );

      void searchCitationAnnotations({
        fetchPolicy: 'cache-and-network',
        variables: {
          paragraphIds,
        },
        onCompleted: ({ getParagraphs }) => {
          // If this is not the most recent request, log and return early
          if (thisRequestId < currentCitationRequestId + 1) {
            diagnostics.debug(
              `[CITATIONS] Ignoring stale request ${thisRequestId}, current is ${currentCitationRequestId}`
            );
            return;
          }

          const annotations: DocumentSearchResult[] = compact(
            getParagraphs.map((paragraph) => {
              const annotationDocumentId = paragraph.file?.pdfDocument?.id;
              if (!annotationDocumentId) {
                diagnostics.error(
                  'No annotation document ID found',
                  undefined,
                  {
                    paragraphId: paragraph.id,
                  }
                );
                return null;
              }

              return {
                documentID: annotationDocumentId,
                documentName: paragraph.file?.pdfDocument?.name,
                PageNumber: paragraph.boundingRegions[0]?.PageNumber ?? 0,
                Polygon: paragraph.boundingRegions[0]?.Polygon ?? [],
                content: paragraph.content,
                id: paragraph.id,
              };
            })
          );

          // set the lastParagraphIds so we know we searched these
          lastCitationParagraphIdsRef.current = [...paragraphIds];
          setAnnotations(annotations);
          const activeAnnotationIndex = 0;
          setActiveAnnotationIndex(activeAnnotationIndex);
          const firstDocumentId =
            annotations[activeAnnotationIndex]?.documentID;
          if (firstDocumentId) {
            setOverrideDocumentId(firstDocumentId);
          }
        },
      });
    },
    [
      documentId,
      annotations,
      lastCitationParagraphIdsRef,
      setAnnotations,
      incrementCitationRequestId,
      currentCitationRequestId,
      searchCitationAnnotations,
      moveActiveAnnotation,
      setActiveAnnotationIndex,
      setOverrideDocumentId,
    ]
  );

  const handleShowCoachmark = useCallback(() => {
    // Some hosts suppress the coachmark entirely (e.g. the chat side content
    // pane, where its instructions don't apply)
    if (!showCitationCoachmark) {
      return;
    }

    // Only set tooltipClicked to true if we should still show the coachmark
    // This prevents the coachmark from showing again after it's been seen
    if (
      shouldShowCoachmark(LOCAL_STORAGE_KEYS.COACHMARK_CITATION_TOOLTIP, 0) &&
      !coachmarkSeen
    ) {
      setTooltipOpen(true);
      setCoachmarkSeen(true);
      incrementCoachmarkSeenCount(
        LOCAL_STORAGE_KEYS.COACHMARK_CITATION_TOOLTIP
      );
    }
  }, [showCitationCoachmark, coachmarkSeen, setCoachmarkSeen]);

  // Handle clicks on the document-level citation button
  const handleDocumentClick = useCallback(() => {
    // Open the edit summary trowser if we're on the document details page and it's closed
    const { openEditSummaryTrowser, isEditSummaryTrowserOpen } =
      documentDetailsContext || {};
    const { openEditMemoTrowser, isEditMemoTrowserOpen } =
      memoDetailsContext || {};

    if (documentDetailsContext && !isEditSummaryTrowserOpen) {
      openEditSummaryTrowser?.();
    } else if (memoDetailsContext && !isEditMemoTrowserOpen) {
      openEditMemoTrowser?.();
    } else {
      handleShowCoachmark();
    }

    setSearchQuery('Document citation evidence');
    searchDocumentForParagraphIds(paragraphIds);
  }, [
    setSearchQuery,
    documentDetailsContext,
    memoDetailsContext,
    handleShowCoachmark,
    searchDocumentForParagraphIds,
    paragraphIds,
  ]);

  // Update the search loading state when the citation query loading state changes
  useEffect(() => {
    setSearchLoading(loading);
  }, [loading, setSearchLoading]);

  // We don't want to show citations everywhere
  if (!showCitations) return null;

  // Hide the evidence icon entirely when there are no citations to resolve.
  // Without this, clicking the icon yields no annotations and the user is left
  // staring at a non-functional control.
  if (paragraphIds.length === 0) return null;

  return (
    <Box
      sx={makeUnprintableStyle({
        display: 'inline-block',
      })}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Coachmark
          id={LOCAL_STORAGE_KEYS.COACHMARK_CITATION_TOOLTIP}
          initialOpen={false}
          title="Click icon to cycle through evidence"
          body="Cycle through the evidence for this document summary bullet by clicking the magnifying glass or prev/next below the document"
          buttonLabel="Got it"
          onClose={() => setTooltipOpen(false)}
          isOpen={tooltipOpen}
          placement="bottom-start"
        >
          <CitationToolTip onClick={handleDocumentClick} />
        </Coachmark>
        {loading && <CircularProgress size={14} />}
      </Stack>
    </Box>
  );
}

const CitationToolTip = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function CitationToolTip({ onClick, ...props }, ref) {
  return (
    <Box
      component="button"
      ref={ref}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick?.(e);
      }}
      {...props}
      sx={{
        borderRadius: 20,
        py: 0.125,
        px: 0.25,
        m: 0,
        border: 'none',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: COLORS.TEAL[100],
        },
        ...props.style,
      }}
    >
      <SearchSmIcon
        size={14}
        // this is marked as important because we were seeing issues with the color being overridden when rendered inside
        // of markdown tables
        color={`${COLORS.TEAL[600]} !important`}
        sx={{ position: 'relative', top: 1, left: -1 }}
      />
    </Box>
  );
});
